import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { USER_MCP_QUERY_KEYS } from '../../api';
import type { AgentMcpItem } from './useAgentMcpStatus';
import { useInstallMcp } from './useInstallMcp';
import { useUpdateMcpCredentials } from './useUpdateMcpCredentials';
import { useConnectMcp } from './useConnectMcp';

interface AuthModalState {
  open: boolean;
  mode: 'install' | 'edit';
  catalogId: string;
  name: string;
}

const CLOSED: AuthModalState = { open: false, mode: 'install', catalogId: '', name: '' };

/**
 * Controller for the chat composer's MCP "Auth" / "Retry" actions. Owns the credentials modal
 * state and the install/re-credit mutations.
 *
 * - `openAuth(item)` — install/(re-)authorize an MCP. A `no_auth` MCP needs no credentials, so it
 *   installs directly (no modal); an `oauth` MCP starts the authorize-popup flow (connect and
 *   reconnect are the same server-side flow — tokens upsert); otherwise the credentials modal
 *   opens (install for a never-installed MCP, edit for one whose stored creds are rejected).
 *   Returns `true` when a modal was opened, so the caller knows whether to dismiss the menu.
 * - `retry(item)` — re-run the live health check in place (busts the cached `/test` result).
 * - `modalProps` — spread onto `<McpHeadersModal />`, rendered at composer root so it survives the
 *   menu unmounting.
 */
export const useChatMcpAuth = () => {
  const queryClient = useQueryClient();
  const installMutation = useInstallMcp();
  const connectMutation = useConnectMcp();
  const updateMutation = useUpdateMcpCredentials();
  const [modal, setModal] = useState<AuthModalState>(CLOSED);

  // For `retry` only: re-runs a row's live health check in place. The mutations (install / connect /
  // update) each invalidate the installed list + this MCP's health themselves on success.
  const refreshHealth = (catalogId: string) =>
    queryClient.invalidateQueries({ queryKey: USER_MCP_QUERY_KEYS.health(catalogId) });

  /** Returns true if a credentials modal was opened, false if the MCP was installed directly. */
  const openAuth = (item: AgentMcpItem): boolean => {
    // no_auth MCPs carry no credentials — install in one click, mirroring the My MCPs marketplace.
    // useInstallMcp already invalidates the installed list + this MCP's health on success.
    if (!item.installed && item.authType === 'no_auth') {
      installMutation.mutate({ mcpCatalogId: item.mcpCatalogId });
      return false;
    }
    // for oauth the same auth process runs for both installed and uninstalled
    // useConnectMcp already invalidates the installed list + this MCP's health on success.
    if (item.authType === 'oauth') {
      connectMutation.mutate({ mcpCatalogId: item.mcpCatalogId });
      return false;
    }

    // for static headers auth type
    setModal({
      open: true,
      mode: item.installed ? 'edit' : 'install',
      catalogId: item.mcpCatalogId,
      name: item.name,
    });
    return true;
  };

  const retry = (item: AgentMcpItem) => void refreshHealth(item.mcpCatalogId);

  const closeModal = () => setModal((prev) => ({ ...prev, open: false }));

  const handleSubmit = (headers: Record<string, string>) => {
    const mutation = modal.mode === 'install' ? installMutation : updateMutation;
    // No manual health refresh here — both mutations invalidate this MCP's health on success.
    mutation.mutate({ mcpCatalogId: modal.catalogId, headers }, { onSuccess: closeModal });
  };

  return {
    openAuth,
    retry,
    modalProps: {
      open: modal.open,
      mode: modal.mode,
      mcpName: modal.name,
      hasExistingHeaders: modal.mode === 'edit',
      isSubmitting: installMutation.isPending || updateMutation.isPending,
      onClose: closeModal,
      onSubmit: handleSubmit,
    },
  };
};
